import { Component, Input, OnInit } from '@angular/core';
import { Document, LookupOptionsData } from 'src/app/jurisdiction/jurisdiction.domain';
import { EnvironmentService } from 'src/app/services/environment.service';
import { FeedInControllabilityComponent } from 'src/app/+grid-application-shared/components/sections/feed-in-controllability/feed-in-controllability.component';
import { RegepCommentsService } from 'src/app/+regep/services/regep-comments.service';
import { RegepOrderProcessService } from 'src/app/+regep/services/regep-order-process.service';
import { RegepProcessStatus } from 'src/app/+regep/entities/enums/regepProcessStatus';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { RegistrationOfEnergyProducerTechnicalData } from 'src/app/+regep/entities/regep-technical-data';
import { ControlContainer, NgForm } from '@angular/forms';
import { RegepProvisioningType } from 'src/app/+regep/entities/enums/regepProvisioningType';
import { AllDropdownKeys } from 'src/app/jurisdiction/jurisdiction.constants';
import { CommonService } from 'src/app/services/common.service';
import { ShippingAddress } from 'src/app/+regep/entities/shipping-address';
import { LocalStorageService } from 'src/app/services/local-storage.service';

@Component({
  selector: 'app-regep-feed-in-controllability',
  templateUrl: './regep-feed-in-controllability.component.html',
  styleUrls: ['./regep-feed-in-controllability.component.scss'],
  viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
})
export class RegepFeedInControllabilityComponent extends FeedInControllabilityComponent implements OnInit {
  @Input() technicalData: RegistrationOfEnergyProducerTechnicalData;

  canModeratorEdit: boolean;
  provisioningType = RegepProvisioningType;
  allTitles: LookupOptionsData[] = this.commonService.getDropdownData(AllDropdownKeys.Title);
  show9EegDetails = this.environmentService.show9EegDetails;
  hideControlAndOnDemandSupplyOfFeedInEnergy = this.environmentService.hideControlAndOnDemandSupplyOfFeedInEnergy;

  public readonly feedInManagementValues = {
    controlAndDemand: 'controlAndDemand',
    controlEEG: 'controlEEG',
    noManagement: 'noManagement',
  };

  constructor(
    commentService: RegepCommentsService,
    environmentService: EnvironmentService,
    private commonService: CommonService,
    private localStorageService: LocalStorageService,
    private readonly orderProcessService: RegepOrderProcessService,
    private form: NgForm
  ) {
    super(commentService, environmentService);
  }

  ngOnInit(): void {
    this.preventLeak(this.getCanModeratorEdit()).subscribe(canEdit => (this.canModeratorEdit = canEdit));
  }

  setData(data: any) {
    this.technicalData = Object.assign(this.technicalData, JSON.parse(data));
  }

  toggleCopyConnectionAddress(event: { checked: boolean }) {
    if (!event.checked) {
      this.technicalData.shipping_address.city = null;
      this.technicalData.shipping_address.post_code = null;
      this.technicalData.shipping_address.street = null;
      this.technicalData.shipping_address.street_number = null;

      return;
    }
    const locationData = this.localStorageService.getItem('location_data');
    if (!locationData) return;
    this.technicalData.shipping_address.city = locationData.city;
    this.technicalData.shipping_address.post_code = locationData.postcode;
    this.technicalData.shipping_address.street = locationData.street;
    this.technicalData.shipping_address.street_number = locationData.street_number;
  }

  private getCanModeratorEdit(): Observable<boolean> {
    if (!this.isModeratorRole()) {
      return of(false);
    }

    return this.orderProcessService.orderProcess$.pipe(map(orderProcess => this.isInEditableStatus(orderProcess.status)));
  }

  private isInEditableStatus(status: RegepProcessStatus): boolean {
    return status < RegepProcessStatus.EngineeringOk;
  }

  feedInManagementChange(feedInManagement: string) {
    this.refreshControlAndDemand();

    if (feedInManagement === this.feedInManagementValues.noManagement) {
      this.technicalData.ripple_control_receiver_preferred_pickup_date = null;
      this.technicalData.feed_in_controllability_contract = null;
      return;
    }

    if (!this.technicalData.feed_in_controllability_contract) {
      this.technicalData.feed_in_controllability_contract = new Document(this.documentTypes.FeedInControllabilityContract);
    }
  }

  refreshControlAndDemand() {
    if (this.technicalData.feed_in_management === this.feedInManagementValues.controlAndDemand) {
      this.technicalData.shipping_address = new ShippingAddress(this.technicalData.application_id);
      return;
    }

    this.technicalData.shipping_address = null;
    this.technicalData.provisioning_type = null;
    this.technicalData.preferred_provisioning_date = null;
  }

  provisioningTypeUpdated(provisioningType: RegepProvisioningType) {
    if (provisioningType === RegepProvisioningType.Shipping) {
      this.technicalData.shipping_address = new ShippingAddress(this.applicationId);
    }
  }

  protected get sectionComment(): string | null {
    return this.technicalData.technical_specifications_sections_state?.feed_in_controllability_comment;
  }

  protected set sectionComment(value: string | null) {
    this.technicalData.technical_specifications_sections_state.feed_in_controllability_comment = value;
  }

  protected get sectionLocked(): boolean | null {
    return this.technicalData.technical_specifications_sections_state?.is_feed_in_controllability_locked;
  }

  protected set sectionLocked(value: boolean | null) {
    this.technicalData.technical_specifications_sections_state.is_feed_in_controllability_locked = value;
  }
}
